import { useEffect } from 'react'
import { usePresignedLogo } from '../hooks/usePresignedLogo'
import { T } from '../theme'

/**
 * ClubLogoOrName — affiche le logo du club, ou son nom en texte si pas de
 * logo (k13).
 *
 * Le bucket S3 logo est PRIVE : `clubs.logo_url` brut renvoie 403 dans un
 * <img>. On passe donc TOUJOURS par GET /club/logo-preview (presigned 1h)
 * via le hook usePresignedLogo (session 1). 3 états :
 *  - loading  -> skeleton (évite le flash "texte puis image")
 *  - logo OK  -> <img src={presigned}>
 *  - null/err -> fallback nom du club en Anton (sauf textFallback=false :
 *                rien, ex. header dashboard où le <h1> porte déjà le nom)
 *
 * Props : { name, size: 'sm'|'lg', textFallback?: bool }
 * Couleur texte = cream (les 2 emplacements — sidebar bas + hero — sont
 * sur fond dark).
 */
const SIZES = {
  sm: { box: 30, font: 13 },
  lg: { box: 88, font: 30 },
}

export default function ClubLogoOrName({ name, size = 'sm', textFallback = true }) {
  const { previewUrl, loading, fetchPreview } = usePresignedLogo()
  const dim = SIZES[size] || SIZES.sm

  useEffect(() => { fetchPreview() }, [fetchPreview])

  if (loading) {
    return (
      <div
        aria-hidden
        style={{
          width: dim.box, height: dim.box, flexShrink: 0,
          background: 'rgba(245,242,235,0.06)',
          animation: 'pulse 1.2s ease-in-out infinite',
        }}
      />
    )
  }

  if (previewUrl) {
    return (
      <img
        src={previewUrl}
        alt={name || 'Logo du club'}
        style={{ height: dim.box, maxWidth: dim.box * 2, objectFit: 'contain', flexShrink: 0, display: 'block' }}
      />
    )
  }

  if (!textFallback) return null

  return (
    <span
      title={name || ''}
      style={{
        fontFamily: T.display, fontSize: dim.font, lineHeight: 1,
        letterSpacing: '.02em', textTransform: 'uppercase',
        color: T.cream, overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap', display: 'block', minWidth: 0,
      }}
    >
      {name || ''}
    </span>
  )
}
